import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  TableSortLabel,
} from '@mui/material';

const EditAddress = ({ setMessage }) => {
  const [addresses, setAddresses] = useState([]);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [editedAddress, setEditedAddress] = useState({});
  const [newAddress, setNewAddress] = useState({
    addressLine1: '',
    accessCode: '',
      });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false); // For editing address
  const [orderBy, setOrderBy] = useState('addressLine1');
  const [order, setOrder] = useState('asc');

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'hvac', 'GenericData', 'ImportantAddresses'));
        const addressesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAddresses(addressesData);
      } catch (error) {
        console.error('Error fetching addresses:', error);
        setMessage('Error fetching addresses. Please try again.');
      }
    };

    fetchAddresses();
  }, [setMessage]);

  const handleEditClick = address => {
    setEditingAddressId(address.id);
    setEditedAddress(address);
    setEditDialogOpen(true); // Open the edit dialog
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setEditedAddress({ ...editedAddress, [name]: value });
  };

  const handleNewAddressChange = e => {
    const { name, value } = e.target;
    setNewAddress({ ...newAddress, [name]: value });
  };

  const handleSaveClick = async () => {
    try {
      const updatedAddress = { ...editedAddress };

      const addressDocRef = doc(db, 'hvac', 'GenericData', 'ImportantAddresses', editingAddressId);
      await updateDoc(addressDocRef, updatedAddress);
      setAddresses(addresses.map(address => (address.id === editingAddressId ? updatedAddress : address)));
      setEditDialogOpen(false); // Close edit dialog
      setMessage('Address updated successfully.');
    } catch (error) {
      console.error('Error updating address:', error);
      setMessage('Error updating address. Please try again.');
    }
  };

  const handleAddAddressClick = async () => {
    try {
      if (!newAddress.addressLine1 || !newAddress.accessCode) {
        setMessage('Please fill out all required fields.');
        return;
      }

      const newAddressWithDisplayName = {
        addressLine1: newAddress.addressLine1,
        accessCode: newAddress.accessCode,
              };

      const docRef = await addDoc(collection(db, 'hvac', 'GenericData', 'ImportantAddresses'), newAddressWithDisplayName);
      setAddresses([...addresses, { id: docRef.id, ...newAddressWithDisplayName }]);

      setNewAddress({
        addressLine1: '',
        accessCode: '',
             });
      setMessage('New address added successfully.');
      setDialogOpen(false);
    } catch (error) {
      console.error('Error adding new address:', error);
      setMessage('Error adding new address. Please try again.');
    }
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);

    const sortedAddresses = [...addresses].sort((a, b) => {
      const aValue = a[column] ?? ''; // Use empty string as fallback if the property is undefined
      const bValue = b[column] ?? '';
      return isAsc
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });

    setAddresses(sortedAddresses);
  };

  return (
    <div>
      <Button variant="contained" color="primary" onClick={() => setDialogOpen(true)}>
        Add New Address
      </Button>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Address</DialogTitle>
        <DialogContent>
          <Box component="form" noValidate autoComplete="off">
            <TextField name="addressLine1" label="Address Line 1" value={newAddress.addressLine1} onChange={handleNewAddressChange} fullWidth margin="normal" />
            <TextField name="accessCode" label="Access Code" value={newAddress.accessCpde} onChange={handleNewAddressChange} fullWidth margin="normal" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddAddressClick} color="primary">
            Add Address
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Address</DialogTitle>
        <DialogContent>
          <Box component="form" noValidate autoComplete="off">
            <TextField name="addressLine1" label="Address Line 1" value={editedAddress.addressLine1} onChange={handleInputChange} fullWidth margin="normal" />
            <TextField name="accessCode" label="accessCode" value={editedAddress.accessCode} onChange={handleInputChange} fullWidth margin="normal" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveClick} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table of Addresses */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel active={orderBy === 'addressLine1'} direction={order} onClick={() => handleSort('addressLine1')}>Address Line 1</TableSortLabel>
              </TableCell>
              <TableCell>Access Code</TableCell>
              
              
            </TableRow>
          </TableHead>
          <TableBody>
            {addresses.map(address => (
              <TableRow key={address.id}>
                <TableCell>{address.addressLine1}</TableCell>
                <TableCell>{address.accessCode}</TableCell>
                               <TableCell>
                  <Button onClick={() => handleEditClick(address)} color="primary">
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default EditAddress;
